// Helper function to check if a field is filled
export const isFieldFilled = (field) => {
  // Basic null/undefined/empty checks
  if (field === null || field === undefined || field === "") return false;
  if (
    typeof field === "string" &&
    (field === "N/A" ||
      field === "Not set" ||
      field === "null" ||
      field.trim() === "")
  )
    return false;

  // For arrays (familyMembers, qualifications, experiences, documents)
  if (Array.isArray(field)) {
    if (field.length === 0) return false;
    // Check if at least one item in the array has meaningful data
    return field.some((item) => {
      if (typeof item === "object" && item !== null) {
        // For objects within arrays, check if they have meaningful data
        const values = Object.values(item);
        return values.some((value) => {
          if (value === null || value === undefined || value === "")
            return false;
          if (typeof value === "string" && value.trim() === "") return false;
          return true;
        });
      }
      // For primitive values in arrays
      if (typeof item === "string" && item.trim() === "") return false;
      return item !== null && item !== undefined && item !== "";
    });
  }

  // For objects (like address)
  if (typeof field === "object" && field !== null) {
    const values = Object.values(field);
    return values.some((value) => {
      if (value === null || value === undefined || value === "") return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    });
  }

  // For primitive values (strings, numbers, booleans)
  return true;
};

export const calculateProfileCompletion = (employee) => {
  if (!employee) return { percentage: 0, pendingSections: [] };

  const sections = [
    {
      name: "Organization Details",
      isComplete: () => {
        const essentialFields = [
          employee.empCode,
          employee.user?.name,
          employee.user?.gender,
          employee.user?.dateOfBirth,
          employee.branchId,
          employee.designationId,
          employee.departmentId,
          employee.subDepartmentId,
          employee.gradeId,
          employee.categoryId,
          employee.reportingManagerId,
          employee.dateOfJoin,
          employee.employmentType,
          employee.employmentStatus,
          employee.user?.phone,
          employee.user?.email,
          employee.email,
          employee.user?.bloodGroup,
          employee.aadharNumber,
          employee.panNumber,
          employee.ctc,
        ];
        const filledCount = essentialFields.filter((field) =>
          isFieldFilled(field)
        ).length;
        return filledCount >= 21; // All 21 fields required
      },
      blankFields: () => {
        const fields = [
          { field: employee.empCode, name: "Employee Code" },
          { field: employee.user?.name, name: "Name" },
          { field: employee.user?.gender, name: "Gender" },
          { field: employee.user?.dateOfBirth, name: "Date of Birth" },
          { field: employee.branchId, name: "Branch" },
          { field: employee.designationId, name: "Designation" },
          { field: employee.departmentId, name: "Department" },
          { field: employee.subDepartmentId, name: "Sub Department" },
          { field: employee.gradeId, name: "Grade" },
          { field: employee.categoryId, name: "Category" },
          { field: employee.reportingManagerId, name: "Reporting Manager" },
          { field: employee.dateOfJoin, name: "Date of Join" },
          { field: employee.employmentType, name: "Employment Type" },
          { field: employee.employmentStatus, name: "Employment Status" },
          { field: employee.user?.phone, name: "Mobile Number" },
          { field: employee.user?.email, name: "Personal Email" },
          { field: employee.email, name: "Official Email" },
          { field: employee.user?.bloodGroup, name: "Blood Group" },
          { field: employee.aadharNumber, name: "Aadhar Number" },
          { field: employee.panNumber, name: "PAN Number" },
          { field: employee.ctc, name: "CTC" },
        ];
        return fields.filter((f) => !isFieldFilled(f.field)).length;
      },
    },
    {
      name: "Personal Details",
      isComplete: () => {
        const essentialFields = [
          employee.user?.maritalStatus,
          employee.address?.address,
          employee.address?.city,
          employee.address?.state,
          employee.address?.pincode,
          employee.address?.permanentAddress,
          employee.address?.permanentCity,
          employee.address?.permanentState,
          employee.address?.permanentPincode,
          employee.emergencyContactName,
          employee.emergencyContactPhone,
          employee.emergencyContactRelation,
          employee.user?.photo,
        ];
        const filledCount = essentialFields.filter((field) =>
          isFieldFilled(field)
        ).length;
        return filledCount >= 13; // All 13 fields required
      },
      blankFields: () => {
        const fields = [
          { field: employee.user?.maritalStatus, name: "Marital Status" },
          { field: employee.fatherName, name: "Father Name" },
          { field: employee.address?.address, name: "Present Address" },
          { field: employee.address?.city, name: "Present City" },
          { field: employee.address?.state, name: "Present State" },
          { field: employee.address?.pincode, name: "Present Pincode" },
          {
            field: employee.address?.permanentAddress,
            name: "Permanent Address",
          },
          { field: employee.address?.permanentCity, name: "Permanent City" },
          { field: employee.address?.permanentState, name: "Permanent State" },
          {
            field: employee.address?.permanentPincode,
            name: "Permanent Pincode",
          },
          {
            field: employee.emergencyContactName,
            name: "Emergency Contact Name",
          },
          {
            field: employee.emergencyContactPhone,
            name: "Emergency Contact Phone",
          },
          {
            field: employee.emergencyContactRelation,
            name: "Emergency Contact Relation",
          },
          { field: employee.user?.photo, name: "Profile Photo" },
        ];
        return fields.filter((f) => !isFieldFilled(f.field)).length;
      },
    },
    {
      name: "Family Details",
      isComplete: () => {
        // If orphan checkbox is checked, section is complete
        if (employee.isOrphan === true) return true;
        // If family members array exists and has data, section is complete
        if (
          employee.familyMembers &&
          Array.isArray(employee.familyMembers) &&
          employee.familyMembers.length > 0
        ) {
          return employee.familyMembers.some(
            (member) =>
              member &&
              typeof member === "object" &&
              Object.values(member).some(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  (typeof value !== "string" || value.trim() !== "")
              )
          );
        }
        return false;
      },
      blankFields: () => {
        if (employee.isOrphan === true) return 0;
        if (
          employee.familyMembers &&
          Array.isArray(employee.familyMembers) &&
          employee.familyMembers.length > 0
        ) {
          return employee.familyMembers.some(
            (member) =>
              member &&
              typeof member === "object" &&
              Object.values(member).some(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  (typeof value !== "string" || value.trim() !== "")
              )
          )
            ? 0
            : 1;
        }
        return 1;
      },
    },
    {
      name: "Bank Details",
      isComplete: () => {
        const essentialFields = [
          employee.bankName,
          employee.accountNumber,
          employee.ifscCode,
          employee.bankBranch,
        ];
        const filledCount = essentialFields.filter((field) =>
          isFieldFilled(field)
        ).length;
        return filledCount >= 4; // All 4 fields required
      },
      blankFields: () => {
        const fields = [
          { field: employee.bankName, name: "Bank Name" },
          { field: employee.accountNumber, name: "Account Number" },
          { field: employee.ifscCode, name: "IFSC Code" },
          { field: employee.bankBranch, name: "Branch" },
        ];
        return fields.filter((f) => !isFieldFilled(f.field)).length;
      },
    },
    {
      name: "Educational Details",
      isComplete: () => {
        // If less than primary checkbox is checked, section is complete
        if (employee.lessThanPrimary === true) return true;
        // If qualifications array exists and has data, section is complete
        if (
          employee.qualifications &&
          Array.isArray(employee.qualifications) &&
          employee.qualifications.length > 0
        ) {
          return employee.qualifications.some(
            (qual) =>
              qual &&
              typeof qual === "object" &&
              Object.values(qual).some(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  (typeof value !== "string" || value.trim() !== "")
              )
          );
        }
        return false;
      },
      blankFields: () => {
        if (employee.lessThanPrimary === true) return 0;
        if (
          employee.qualifications &&
          Array.isArray(employee.qualifications) &&
          employee.qualifications.length > 0
        ) {
          return employee.qualifications.some(
            (qual) =>
              qual &&
              typeof qual === "object" &&
              Object.values(qual).some(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  (typeof value !== "string" || value.trim() !== "")
              )
          )
            ? 0
            : 1;
        }
        return 1;
      },
    },
    {
      name: "Professional Details",
      isComplete: () => {
        // If fresher checkbox is checked, section is complete
        if (employee.isFresher === true) return true;
        // If experiences array exists and has data, section is complete
        if (
          employee.experiences &&
          Array.isArray(employee.experiences) &&
          employee.experiences.length > 0
        ) {
          return employee.experiences.some(
            (exp) =>
              exp &&
              typeof exp === "object" &&
              Object.values(exp).some(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  (typeof value !== "string" || value.trim() !== "")
              )
          );
        }
        return false;
      },
      blankFields: () => {
        if (employee.isFresher === true) return 0;
        if (
          employee.experiences &&
          Array.isArray(employee.experiences) &&
          employee.experiences.length > 0
        ) {
          return employee.experiences.some(
            (exp) =>
              exp &&
              typeof exp === "object" &&
              Object.values(exp).some(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  (typeof value !== "string" || value.trim() !== "")
              )
          )
            ? 0
            : 1;
        }
        return 1;
      },
    },
    {
      name: "Statutory Details",
      isComplete: () => {
        const essentialFields = [
          employee.pfUANNumber,
          employee.esiIPNumber,
          employee.esiState,
          employee.esiDistrict,
          employee.esiDispensary,
        ];
        const filledCount = essentialFields.filter((field) =>
          isFieldFilled(field)
        ).length;
        return filledCount >= 5; // All 5 fields required
      },
      blankFields: () => {
        const fields = [
          { field: employee.pfUANNumber, name: "PF UAN Number" },
          { field: employee.esiIPNumber, name: "ESI IP Number" },
          { field: employee.esiState, name: "ESI State" },
          { field: employee.esiDistrict, name: "ESI District" },
          { field: employee.esiDispensary, name: "ESI Dispensary" },
        ];
        return fields.filter((f) => !isFieldFilled(f.field)).length;
      },
    },
    {
      name: "Documents",
      isComplete: () => {
        // If documents array exists and has data, section is complete
        if (
          employee.documents &&
          Array.isArray(employee.documents) &&
          employee.documents.length > 0
        ) {
          return employee.documents.some(
            (doc) =>
              doc &&
              typeof doc === "object" &&
              Object.values(doc).some(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  (typeof value !== "string" || value.trim() !== "")
              )
          );
        }
        return false;
      },
      blankFields: () => {
        if (
          employee.documents &&
          Array.isArray(employee.documents) &&
          employee.documents.length > 0
        ) {
          return employee.documents.some(
            (doc) =>
              doc &&
              typeof doc === "object" &&
              Object.values(doc).some(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  (typeof value !== "string" || value.trim() !== "")
              )
          )
            ? 0
            : 1;
        }
        return 1;
      },
    },
  ];

  let completedSections = 0;
  const pendingSections = [];

  sections.forEach((section) => {
    const isComplete = section.isComplete();
    const blankFields = section.blankFields();

    if (isComplete) {
      completedSections++;
    } else {
      // Calculate actual completed fields and total fields for each section
      let completedFields, totalFields, percentage;

      if (section.name === "Organization Details") {
        const fields = [
          { field: employee.empCode, name: "Employee Code" },
          { field: employee.user?.name, name: "Name" },
          { field: employee.user?.gender, name: "Gender" },
          { field: employee.user?.dateOfBirth, name: "Date of Birth" },
          { field: employee.branchId, name: "Branch" },
          { field: employee.designationId, name: "Designation" },
          { field: employee.departmentId, name: "Department" },
          { field: employee.subDepartmentId, name: "Sub Department" },
          { field: employee.gradeId, name: "Grade" },
          { field: employee.categoryId, name: "Category" },
          { field: employee.reportingManagerId, name: "Reporting Manager" },
          { field: employee.dateOfJoin, name: "Date of Join" },
          { field: employee.employmentType, name: "Employment Type" },
          { field: employee.employmentStatus, name: "Employment Status" },
          { field: employee.user?.phone, name: "Mobile Number" },
          { field: employee.user?.email, name: "Personal Email" },
          { field: employee.email, name: "Official Email" },
          { field: employee.user?.bloodGroup, name: "Blood Group" },
          { field: employee.aadharNumber, name: "Aadhar Number" },
          { field: employee.panNumber, name: "PAN Number" },
          { field: employee.ctc, name: "CTC" },
        ];
        completedFields = fields.filter((f) => isFieldFilled(f.field)).length;
        totalFields = fields.length;
        percentage = Math.round((completedFields / totalFields) * 100);
      } else if (section.name === "Personal Details") {
        const fields = [
          { field: employee.user?.maritalStatus, name: "Marital Status" },
          { field: employee.fatherName, name: "Father Name" },
          { field: employee.address?.address, name: "Present Address" },
          { field: employee.address?.city, name: "Present City" },
          { field: employee.address?.state, name: "Present State" },
          { field: employee.address?.pincode, name: "Present Pincode" },
          {
            field: employee.address?.permanentAddress,
            name: "Permanent Address",
          },
          { field: employee.address?.permanentCity, name: "Permanent City" },
          { field: employee.address?.permanentState, name: "Permanent State" },
          {
            field: employee.address?.permanentPincode,
            name: "Permanent Pincode",
          },
          {
            field: employee.emergencyContactName,
            name: "Emergency Contact Name",
          },
          {
            field: employee.emergencyContactPhone,
            name: "Emergency Contact Phone",
          },
          {
            field: employee.emergencyContactRelation,
            name: "Emergency Contact Relation",
          },
          { field: employee.user?.photo, name: "Profile Photo" },
        ];
        completedFields = fields.filter((f) => isFieldFilled(f.field)).length;
        totalFields = fields.length;
        percentage = Math.round((completedFields / totalFields) * 100);
      } else if (section.name === "Bank Details") {
        const fields = [
          { field: employee.bankName, name: "Bank Name" },
          { field: employee.accountNumber, name: "Account Number" },
          { field: employee.ifscCode, name: "IFSC Code" },
          { field: employee.bankBranch, name: "Branch" },
        ];
        completedFields = fields.filter((f) => isFieldFilled(f.field)).length;
        totalFields = fields.length;
        percentage = Math.round((completedFields / totalFields) * 100);
      } else if (section.name === "Statutory Details") {
        const fields = [
          { field: employee.pfUANNumber, name: "PF UAN Number" },
          { field: employee.esiIPNumber, name: "ESI IP Number" },
          { field: employee.esiState, name: "ESI State" },
          { field: employee.esiDistrict, name: "ESI District" },
          { field: employee.esiDispensary, name: "ESI Dispensary" },
        ];
        completedFields = fields.filter((f) => isFieldFilled(f.field)).length;
        totalFields = fields.length;
        percentage = Math.round((completedFields / totalFields) * 100);
      } else {
        // For single-field sections (Family, Educational, Professional, Documents)
        completedFields = isFieldFilled(
          section.name === "Family Details"
            ? employee.familyMembers
            : section.name === "Educational Details"
            ? employee.qualifications
            : section.name === "Professional Details"
            ? employee.experiences
            : employee.documents
        )
          ? 1
          : 0;
        totalFields = 1;
        percentage = completedFields * 100;
      }

      pendingSections.push({
        name: section.name,
        completed: completedFields,
        total: totalFields,
        blankFields: blankFields,
        percentage: percentage,
      });
    }
  });

  // Calculate percentage: completed sections / total sections
  const percentage = Math.round((completedSections / sections.length) * 100);

  return { percentage, pendingSections };
};

export const getNameByIdFromList = (id, list, employeeData = null, fieldName = null) => {
  // First check if employee data contains the nested object with the name
  if (employeeData && fieldName) {
    const nestedField = getNestedFieldName(fieldName);
    if (nestedField && employeeData[nestedField] && employeeData[nestedField].name) {
      return employeeData[nestedField].name.trim();
    }
  }
  
  // Fallback to list lookup
  if (!list || !Array.isArray(list)) return "-";
  const item = list.find((obj) => obj.id === id);
  return item && item.name ? item.name.trim() : "-";
};

// Helper to map field names to their nested object equivalents
const getNestedFieldName = (fieldName) => {
  const fieldMap = {
    'branch': 'branch',
    'department': 'department', 
    'designation': 'designation',
    'subDepartment': 'subDepartment',
    'grade': 'grade',
    'category': 'category',
    'reportingManager': 'reportingManager'
  };
  return fieldMap[fieldName];
};

/** Format date as DD-MM-YYYY for display in Employee module */
export const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return "";
  }
};

/** Format name for display: first letter of each word uppercase, rest lowercase, spaces preserved. e.g. "kranthi dev sai" → "Kranthi Dev Sai" */
export const toTitleCase = (str) => {
  if (typeof str !== "string") return "";
  return str
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .map((word) =>
      word.length
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : ""
    )
    .join(" ");
};
